import { WEEKDAYS } from "resources/scheduled-workflows/types"
import type { BlockedReason, HourlySchedule, RecurringSchedule, ScheduledWorkflowListItem, ScheduledWorkflowSchedule, Weekday, WeeklySchedule } from "resources/scheduled-workflows/types"
import { BLOCKED_COPY, MANUAL_ONLY_ACTIVE_COPY, MANUAL_ONLY_PAUSED_COPY, PAUSED_COPY } from "./blocked-copy"

export const DAY_LABELS: Record<Weekday, string> = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" }

const UNKNOWN_SCHEDULE_FALLBACK = "Scheduled"

const MINIMUM_RANGE_LENGTH = 3

const DAY_MILLISECONDS = 24 * 60 * 60 * 1000

const MINUTE_MILLISECONDS = 60 * 1000

const HOUR_MINUTES = 60

const LOOKAHEAD_DAYS = 15

export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

function weekStartIndex(included: boolean[]): number {
  const last = included.length - 1
  if (!included[0] || !included[last]) return 0
  let start = last
  while (start > 0 && included[start - 1]) start -= 1
  if (start === 0) return 0
  let end = 0
  while (included[end + 1]) end += 1
  return included.length - start + end + 1 >= MINIMUM_RANGE_LENGTH ? start : 0
}

function formatDays(days: Weekday[]): string {
  const included = WEEKDAYS.map((day) => days.includes(day))
  const start = weekStartIndex(included)
  const dayIndexes = WEEKDAYS.map((_, offset) => (start + offset) % WEEKDAYS.length).filter((index) => included[index])
  const dayRanges: number[][] = []
  dayIndexes.forEach((index) => {
    const lastRange = dayRanges[dayRanges.length - 1]
    if (lastRange && (lastRange[lastRange.length - 1] + 1) % WEEKDAYS.length === index) lastRange.push(index)
    else dayRanges.push([index])
  })
  return dayRanges
    .map((run) => (run.length >= MINIMUM_RANGE_LENGTH
      ? `${DAY_LABELS[WEEKDAYS[run[0]]]}–${DAY_LABELS[WEEKDAYS[run[run.length - 1]]]}`
      : run.map((index) => DAY_LABELS[WEEKDAYS[index]]).join(", ")))
    .join(", ")
}

function formatClockMinutes(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / HOUR_MINUTES)
  const minutes = totalMinutes % HOUR_MINUTES
  const period = hours < 12 ? "AM" : "PM"
  const displayHours = hours % 12 === 0 ? 12 : hours % 12
  return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`
}

export function formatClockTime(time: string): string {
  return formatClockMinutes(minutesOf(time))
}

function weekdayOfDay(day: number): Weekday {
  return WEEKDAYS[(new Date(day * DAY_MILLISECONDS).getUTCDay() + 6) % 7]
}

function instantAt(day: number, minutes: number, zone: string): number {
  const wallClock = day * DAY_MILLISECONDS + minutes * MINUTE_MILLISECONDS
  return zonedInstants(wallClock, zone)[0] ?? wallClock - zoneOffsetMilliseconds(wallClock, zone)
}

function nextOccurrenceDay(weekday: Weekday, lastMinutes: number, zone: string, from: Date): number {
  const today = calendarDayOf(from, zone)
  for (let day = today; day < today + WEEKDAYS.length + 1; day += 1) {
    if (weekdayOfDay(day) === weekday && instantAt(day, lastMinutes, zone) >= from.getTime()) return day
  }
  return today
}

interface ViewerClock {
  day: number
  minutes: number
}

function viewerClockOf(instant: number, viewerZone: string): ViewerClock {
  const wallClock = instant + zoneOffsetMilliseconds(instant, viewerZone)
  const day = Math.floor(wallClock / DAY_MILLISECONDS)
  return { day, minutes: (wallClock - day * DAY_MILLISECONDS) / MINUTE_MILLISECONDS }
}

function groupDays<T>(days: Weekday[], keyOf: (day: Weekday) => { key: string, viewerDay: Weekday, value: T }): { value: T, days: Weekday[] }[] {
  const groups = new Map<string, { value: T, days: Weekday[] }>()
  days.forEach((day) => {
    const { key, viewerDay, value } = keyOf(day)
    const group = groups.get(key) ?? { value, days: [] }
    group.days.push(viewerDay)
    groups.set(key, group)
  })
  return [...groups.values()]
}

export interface ScheduleFormatOptions {
  now: Date
  nextRunAt?: string | null
}

function scheduleConversionInstant(schedule: RecurringSchedule, { now, nextRunAt }: ScheduleFormatOptions): Date {
  const nextRun = nextRunAt ?? computeNextRunAt(schedule, now)
  return nextRun === null ? now : new Date(nextRun)
}

function formatWeeklySchedule(schedule: WeeklySchedule, viewerZone: string, options: ScheduleFormatOptions): string {
  if (schedule.timezone === viewerZone) return `${formatDays(schedule.days)} · ${formatClockTime(schedule.time)}`
  const from = scheduleConversionInstant(schedule, options)
  const start = minutesOf(schedule.time)
  return groupDays(schedule.days, (day) => {
    const clock = viewerClockOf(instantAt(nextOccurrenceDay(day, start, schedule.timezone, from), start, schedule.timezone), viewerZone)
    return { key: String(clock.minutes), viewerDay: weekdayOfDay(clock.day), value: clock.minutes }
  })
    .sort((first, second) => first.value - second.value)
    .map((group) => `${formatDays(group.days)} · ${formatClockMinutes(group.value)}`)
    .join("; ")
}

function formatWindowEdge(minutes: number): string {
  return formatClockMinutes(minutes).replace(":00 ", " ")
}

function hourlyInterval(schedule: HourlySchedule): string {
  return schedule.everyHours === 1 ? "Every hour" : `Every ${schedule.everyHours} hours`
}

function formatHourlySchedule(schedule: HourlySchedule, viewerZone: string, options: ScheduleFormatOptions): string {
  const interval = hourlyInterval(schedule)
  if (schedule.timezone === viewerZone) return `${interval} · ${formatWindowEdge(minutesOf(schedule.start))}–${formatWindowEdge(minutesOf(schedule.end))} · ${formatDays(schedule.days)}`
  const from = scheduleConversionInstant(schedule, options)
  const start = minutesOf(schedule.start)
  const end = minutesOf(schedule.end)
  return groupDays(schedule.days, (day) => {
    const scheduleDay = nextOccurrenceDay(day, end, schedule.timezone, from)
    const opening = viewerClockOf(instantAt(scheduleDay, start, schedule.timezone), viewerZone)
    const closing = viewerClockOf(instantAt(scheduleDay, end, schedule.timezone), viewerZone)
    const endsNextDay = closing.day > opening.day
    return { key: `${opening.minutes}|${closing.minutes}|${endsNextDay}`, viewerDay: weekdayOfDay(opening.day), value: { opening: opening.minutes, closing: closing.minutes, endsNextDay } }
  })
    .sort((first, second) => first.value.opening - second.value.opening)
    .map(({ value, days }) => `${interval} · ${formatWindowEdge(value.opening)}–${formatWindowEdge(value.closing)}${value.endsNextDay ? " next day" : ""} · ${formatDays(days)}`)
    .join("; ")
}

function formatConvertedSchedule(schedule: ScheduledWorkflowSchedule, viewerZone: string, options: ScheduleFormatOptions): string {
  switch (schedule?.type) {
    case "weekly":
      return formatWeeklySchedule(schedule, viewerZone, options)
    case "hourly":
      return formatHourlySchedule(schedule, viewerZone, options)
    default:
      return UNKNOWN_SCHEDULE_FALLBACK
  }
}

export function formatSchedule(schedule: ScheduledWorkflowSchedule, viewerZone: string, options: ScheduleFormatOptions): string {
  try {
    return formatConvertedSchedule(schedule, viewerZone, options)
  } catch (error) {
    if (error instanceof RangeError) return formatScheduleDays(schedule)
    throw error
  }
}

export function formatScheduleDays(schedule: ScheduledWorkflowSchedule): string {
  switch (schedule?.type) {
    case "weekly":
      return formatDays(schedule.days)
    case "hourly":
      return `${hourlyInterval(schedule)} · ${formatDays(schedule.days)}`
    default:
      return UNKNOWN_SCHEDULE_FALLBACK
  }
}

function numericPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number {
  return Number(parts.find((part) => part.type === type)?.value)
}

function calendarDayOf(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "numeric", day: "numeric" }).formatToParts(date)
  return Date.UTC(numericPart(parts, "year"), numericPart(parts, "month") - 1, numericPart(parts, "day")) / DAY_MILLISECONDS
}

function formatDayAndTime(date: Date, dayDifference: number, adjacentDayLabel: string, viewerZone: string): string {
  const clockTime = new Intl.DateTimeFormat("en-US", { timeZone: viewerZone, hour: "numeric", minute: "2-digit" }).format(date)
  const day = dayDifference === 0
    ? "today"
    : dayDifference === 1
      ? adjacentDayLabel
      : new Intl.DateTimeFormat("en-US", dayDifference > 1 && dayDifference < 7 ? { timeZone: viewerZone, weekday: "short" } : { timeZone: viewerZone, month: "short", day: "numeric" }).format(date)
  return `${day}, ${clockTime}`
}

function zoneOffsetMilliseconds(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" }).formatToParts(new Date(instant))
  return Date.UTC(numericPart(parts, "year"), numericPart(parts, "month") - 1, numericPart(parts, "day"), numericPart(parts, "hour"), numericPart(parts, "minute")) - instant
}

function zonedInstants(wallClock: number, timeZone: string): number[] {
  const instants = [wallClock - DAY_MILLISECONDS, wallClock + DAY_MILLISECONDS]
    .map((nearby) => wallClock - zoneOffsetMilliseconds(nearby, timeZone))
    .filter((instant) => instant + zoneOffsetMilliseconds(instant, timeZone) === wallClock)
  return [...new Set(instants)].sort((first, second) => first - second)
}

function minutesOf(time: string): number {
  const [hours, minutes] = time.split(":").map(Number)
  return hours * HOUR_MINUTES + minutes
}

function runMinutes(schedule: RecurringSchedule): number[] {
  if (schedule.type === "weekly") return [minutesOf(schedule.time)]
  const minutes: number[] = []
  for (let minute = minutesOf(schedule.start); minute <= minutesOf(schedule.end); minute += schedule.everyHours * HOUR_MINUTES) minutes.push(minute)
  return minutes
}

export function computeNextRunAt(schedule: RecurringSchedule, now: Date): string | null {
  const today = calendarDayOf(now, schedule.timezone)
  for (let day = today; day < today + LOOKAHEAD_DAYS; day += 1) {
    const nextRun = schedule.days.includes(weekdayOfDay(day))
      ? runMinutes(schedule).flatMap((minute) => zonedInstants(day * DAY_MILLISECONDS + minute * MINUTE_MILLISECONDS, schedule.timezone)).find((instant) => instant > now.getTime())
      : undefined
    if (nextRun !== undefined) return new Date(nextRun).toISOString()
  }
  return null
}

export function formatRunTime(runAt: string, now: Date, viewerZone: string): string {
  const run = new Date(runAt)
  return formatDayAndTime(run, calendarDayOf(run, viewerZone) - calendarDayOf(now, viewerZone), "tomorrow", viewerZone)
}

export function formatNextRun(nextRunAt: string, now: Date, viewerZone: string = viewerTimeZone()): string {
  return `Next: ${formatRunTime(nextRunAt, now, viewerZone)}`
}

export function formatReportTime(time: string, now: Date, viewerZone: string = viewerTimeZone()): string {
  const reportTime = new Date(time)
  return formatDayAndTime(reportTime, calendarDayOf(now, viewerZone) - calendarDayOf(reportTime, viewerZone), "yesterday", viewerZone)
}

export function formatResetTime(reset: Date, now: Date, viewerZone: string): string {
  const time = new Intl.DateTimeFormat("en-US", { timeZone: viewerZone, hour: "numeric", minute: "2-digit" }).format(reset)
  if (calendarDayOf(reset, viewerZone) === calendarDayOf(now, viewerZone)) return time
  return `${new Intl.DateTimeFormat("en-US", { timeZone: viewerZone, month: "short", day: "numeric" }).format(reset)} at ${time}`
}

export function formatResetsAt(resetsAt: string, now: Date, viewerZone: string): string | null {
  const reset = new Date(resetsAt)
  return Number.isNaN(reset.getTime()) ? null : formatResetTime(reset, now, viewerZone)
}

export function nextBudgetReset(instant: Date): Date {
  return new Date(Date.UTC(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate() + 1))
}

export function pausedUntilCopy(reset: Date, now: Date, viewerZone: string): string {
  return `Paused until ${formatResetTime(reset, now, viewerZone)}, when the daily limit resets. It resumes on its own.`
}

export function overBudgetCopy(blockedAt: Date, now: Date, viewerZone: string): string {
  return pausedUntilCopy(nextBudgetReset(blockedAt), now, viewerZone)
}

export function blockedCopy(reason: BlockedReason, blockedAt: Date, now: Date, viewerZone: string): string {
  if (reason === "over_budget") return overBudgetCopy(blockedAt, now, viewerZone)
  return BLOCKED_COPY[reason] ?? PAUSED_COPY
}

export function scheduleStatusLine(workflow: ScheduledWorkflowListItem, now: Date, viewerZone: string = viewerTimeZone(), listedAt: Date = now): string | null {
  if (workflow.archivedAt != null) return null
  if (workflow.blockedReason != null) return blockedCopy(workflow.blockedReason, listedAt, now, viewerZone)
  if (workflow.schedule == null) return workflow.status === "paused" ? MANUAL_ONLY_PAUSED_COPY : MANUAL_ONLY_ACTIVE_COPY
  if (workflow.status === "paused") return PAUSED_COPY
  return workflow.nextRunAt == null ? null : formatNextRun(workflow.nextRunAt, now, viewerZone)
}

export function emailResultsLine(emailResults: boolean): string {
  return `Email me results: ${emailResults ? "on" : "off"}`
}
